import React, { useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Tabs,
  Tab,
  Chip,
} from '@mui/material';
import PhoneInTalkIcon from '@mui/icons-material/PhoneInTalk';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import { withTranslation } from 'react-i18next';
import { usePaginatedList } from '../../hooks/usePaginatedList';
import PaginationControls from '../shared/PaginationControls';
import DataTable from '../shared/DataTable';
import AiPhoneCallDetailDialog from './AiPhoneCallDetailDialog';

const STATUS_TABS = [
  { value: '', labelKey: 'aiPhone.all' },
  { value: 'new', labelKey: 'aiPhone.new' },
  { value: 'contacted', labelKey: 'aiPhone.contacted' },
  { value: 'done', labelKey: 'aiPhone.done' },
];

const STATUS_CONFIG = {
  new: { labelKey: 'aiPhone.statusNew', color: '#4299e1', bg: '#ebf4ff' },
  contacted: { labelKey: 'aiPhone.statusContacted', color: '#f6ad55', bg: '#fef3e2' },
  done: { labelKey: 'aiPhone.statusDone', color: '#48bb78', bg: '#e6ffed' },
};

function formatDuration(seconds) {
  if (!seconds && seconds !== 0) return '-';
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${String(secs).padStart(2, '0')}`;
}

function formatCalledAt(dateStr) {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  const now = new Date();
  const isToday = date.toDateString() === now.toDateString();

  const time = date.toLocaleTimeString('fi-FI', { hour: '2-digit', minute: '2-digit' });
  if (isToday) {
    return `Tänään ${time}`;
  }
  return `${date.toLocaleDateString('fi-FI', { day: 'numeric', month: 'numeric' })} ${time}`;
}

function truncate(str, maxLen = 60) {
  if (!str) return '';
  return str.length > maxLen ? str.substring(0, maxLen) + '...' : str;
}

function buildVehicleDisplay(call) {
  const parts = [call.vehicle_make, call.vehicle_model].filter(Boolean);
  const display = parts.join(' ');
  if (call.vehicle_registration) {
    return display ? `${display} (${call.vehicle_registration})` : call.vehicle_registration;
  }
  return display || '';
}

function StatusChip({ status, t }) {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.new;
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        padding: '4px 12px',
        borderRadius: '20px',
        backgroundColor: config.bg,
        color: config.color,
        fontWeight: 500,
        fontSize: '13px',
        whiteSpace: 'nowrap',
      }}
    >
      <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: config.color }} />
      {t(config.labelKey)}
    </Box>
  );
}

const MainAiPhoneView = ({ t }) => {
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedCallId, setSelectedCallId] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const {
    data: callsData,
    loading: callsLoading,
    page: callsPage,
    pageSize: callsPageSize,
    totalCount: callsTotalCount,
    totalPages: callsTotalPages,
    setPage: callsSetPage,
    setPageSize: callsSetPageSize,
    setFilters,
    refetch: callsRefetch,
  } = usePaginatedList('/api/ai-phone-calls/', {
    initialFilters: {},
    defaultSortField: '',
    defaultSortDirection: 'desc',
    defaultPageSize: 50,
  });

  React.useEffect(() => {
    const f = {};
    if (statusFilter) f.status = statusFilter;
    setFilters(f);
  }, [statusFilter, setFilters]);

  const handleTabChange = useCallback((event, newValue) => {
    setStatusFilter(newValue);
  }, []);

  const handleRowClick = useCallback((callId) => {
    setSelectedCallId(callId);
    setDetailOpen(true);
  }, []);

  const handleDetailClose = useCallback(() => {
    setDetailOpen(false);
    setSelectedCallId(null);
  }, []);

  const handleDetailSaved = useCallback(() => {
    callsRefetch();
  }, [callsRefetch]);

  return (
    <Box data-testid="ai-phone-page" sx={{ p: { xs: 2, md: 3 } }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h5"
          sx={{
            fontWeight: 600,
            fontFamily: "'Plus Jakarta Sans', sans-serif",
          }}
        >
          {t('aiPhone.title')}
        </Typography>
      </Box>

      {/* Status filter tabs */}
      <Paper elevation={0} sx={{ mb: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
        <Tabs
          value={statusFilter}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ minHeight: 48 }}
        >
          {STATUS_TABS.map((tab) => (
            <Tab
              key={tab.value}
              value={tab.value}
              label={t(tab.labelKey)}
              sx={{ textTransform: 'none', fontWeight: 500, minHeight: 48 }}
            />
          ))}
        </Tabs>
      </Paper>

      {/* Desktop Table */}
      <DataTable
        columns={[
          {
            key: 'status',
            label: t('aiPhone.status'),
            width: '1%',
            noWrap: true,
            render: (call) => <StatusChip status={call.status} t={t} />,
          },
          {
            key: 'urgency',
            label: '',
            width: '1%',
            hideBelow: 'lg',
            render: (call) =>
              call.urgency === 'urgent' ? (
                <Chip
                  size="small"
                  label={t('aiPhone.urgencyUrgent')}
                  sx={{ bgcolor: '#fee2e2', color: '#dc2626', fontWeight: 500, fontSize: '11px', height: 22 }}
                />
              ) : null,
          },
          {
            key: 'called_at',
            label: t('aiPhone.calledAt'),
            width: '1%',
            noWrap: true,
            render: (call) => (
              <Typography variant="body2" color="text.secondary" noWrap>
                {formatCalledAt(call.called_at)}
              </Typography>
            ),
          },
          {
            key: 'caller',
            label: t('aiPhone.caller'),
            noWrap: true,
            render: (call) => (
              <Typography variant="body2" fontWeight={500} noWrap>
                {call.caller_name || call.caller_phone || t('aiPhone.unknownCaller')}
              </Typography>
            ),
          },
          {
            key: 'vehicle',
            label: t('aiPhone.vehicle'),
            noWrap: true,
            hideBelow: 'lg',
            render: (call) => (
              <Typography variant="body2" color="text.secondary" noWrap>
                {buildVehicleDisplay(call)}
              </Typography>
            ),
          },
          {
            key: 'issue',
            label: t('aiPhone.issue'),
            maxWidth: 250,
            render: (call) => (
              <Typography variant="body2" color="text.secondary" noWrap>
                {truncate(call.issue_description)}
              </Typography>
            ),
          },
          {
            key: 'timing',
            label: t('aiPhone.timing'),
            width: '1%',
            noWrap: true,
            hideBelow: 'lg',
            render: (call) => (
              <Typography variant="body2" color="text.secondary" noWrap>
                {call.preferred_timing || ''}
              </Typography>
            ),
          },
          {
            key: 'duration',
            label: t('aiPhone.duration'),
            width: '1%',
            noWrap: true,
            hideBelow: 'lg',
            render: (call) => (
              <Typography variant="body2" color="text.secondary">
                {formatDuration(call.call_duration_seconds)}
              </Typography>
            ),
          },
        ]}
        data={callsData}
        loading={callsLoading}
        onRowClick={(call) => handleRowClick(call.id)}
        emptyIcon={<PhoneInTalkIcon />}
        emptyTitle={t('aiPhone.noCallsYet')}
        emptyDescription={t('aiPhone.noCallsDescription')}
        testIdPrefix="ai-phone-call-row"
        tableTestId="ai-phone-calls-table"
        pagination={{
          page: callsPage,
          totalPages: callsTotalPages,
          totalCount: callsTotalCount,
          pageSize: callsPageSize,
          onPageChange: callsSetPage,
          onPageSizeChange: callsSetPageSize,
        }}
      />

      {/* Mobile Cards */}
      <Box sx={{ display: { xs: 'block', lg: 'none' }, position: 'relative' }}>
        {callsLoading && callsData.length > 0 && (
          <Box
            sx={{
              position: 'absolute',
              top: 0, left: 0, right: 0, bottom: 0,
              bgcolor: 'rgba(255, 255, 255, 0.7)',
              zIndex: 1,
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'center',
              pt: 8,
            }}
          >
            <CircularProgress size={32} />
          </Box>
        )}
        {callsLoading && callsData.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : callsData.length === 0 ? (
          <Paper elevation={0} sx={{ p: 6, textAlign: 'center', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
            <PhoneInTalkIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {t('aiPhone.noCallsYet')}
            </Typography>
            <Typography variant="body2" color="text.disabled">
              {t('aiPhone.noCallsDescription')}
            </Typography>
          </Paper>
        ) : (
          <>
            {callsData.map((call) => (
              <Paper
                key={call.id}
                elevation={0}
                onClick={() => handleRowClick(call.id)}
                sx={{
                  p: 2,
                  mb: 1.5,
                  cursor: 'pointer',
                  '&:hover': { bgcolor: 'action.hover' },
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <StatusChip status={call.status} t={t} />
                    {call.urgency === 'urgent' && (
                      <FiberManualRecordIcon sx={{ fontSize: 12, color: 'error.main' }} />
                    )}
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    {formatCalledAt(call.called_at)}
                  </Typography>
                </Box>
                <Typography variant="body2" fontWeight={500}>
                  {call.caller_name || call.caller_phone || t('aiPhone.unknownCaller')}
                </Typography>
                {buildVehicleDisplay(call) && (
                  <Typography variant="body2" color="text.secondary">
                    {buildVehicleDisplay(call)}
                  </Typography>
                )}
                {call.issue_description && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {truncate(call.issue_description, 80)}
                  </Typography>
                )}
              </Paper>
            ))}
            <PaginationControls
              page={callsPage}
              totalPages={callsTotalPages}
              totalCount={callsTotalCount}
              pageSize={callsPageSize}
              onPageChange={callsSetPage}
              onPageSizeChange={callsSetPageSize}
            />
          </>
        )}
      </Box>

      {/* Detail dialog */}
      {detailOpen && selectedCallId && (
        <AiPhoneCallDetailDialog
          open={detailOpen}
          onClose={handleDetailClose}
          callId={selectedCallId}
          onSaved={handleDetailSaved}
          t={t}
        />
      )}
    </Box>
  );
};

export default withTranslation()(MainAiPhoneView);
